<div class="clusters-container">

  <div class="clusters-header">
    <div class="clusters-header-left">
      <button class="back-button" (click)="onBackClick()" aria-label="Back to chat">
        <img src="assets/icons/collection-arrow.svg" alt="Back" class="back-arrow-icon">
      </button>
      <h1 class="clusters-title">Clusters</h1>
    </div>

    <div class="header-actions">
      <button
        class="primary-button"
        (click)="clusterize()"
        [disabled]="isClusterizing"
        title="Group every chunk in the index by semantic similarity">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="button-icon">
          <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
        </svg>
        {{ isClusterizing ? 'Clustering...' : 'Clusterize' }}
      </button>
    </div>
  </div>

  <!-- Single source of truth for the progress card's markup, so the top-banner and
       centered placements below can never drift apart visually. -->
  <ng-template #progressCard>
    <div class="progress-card">
      <div class="progress-card-header">
        <span class="progress-stage-label">{{ progressStageLabel }}</span>
        <span class="progress-percent" *ngIf="!progressIsIndeterminate">{{ progressPercentRounded }}%</span>
      </div>
      <div class="progress-track">
        <div
          class="progress-fill"
          [class.indeterminate]="progressIsIndeterminate"
          [style.width.%]="progressIsIndeterminate ? null : progressPercent">
        </div>
      </div>
    </div>
  </ng-template>

  <!-- Clusters already on screen: a re-cluster runs behind them, so progress goes in a
       banner up top rather than displacing the list. -->
  <div class="progress-section" *ngIf="showProgressBar && clusters.length > 0">
    <ng-container *ngTemplateOutlet="progressCard"></ng-container>
  </div>

  <div class="banner banner-error" *ngIf="clusterError">{{ clusterError }}</div>
  <div class="banner banner-info" *ngIf="clusterizeSummary && !clusterError">{{ clusterizeSummary }}</div>
  <div class="banner banner-success" *ngIf="generateSuccess">{{ generateSuccess }}</div>
  <div class="banner banner-error" *ngIf="clusterGenerateError">{{ clusterGenerateError }}</div>

  <div class="loading-state" *ngIf="isLoading">
    <div class="loading-spinner"></div>
  </div>

  <div class="empty-state" *ngIf="!isLoading && loadError">
    <p class="empty-message">{{ loadError }}</p>
    <button class="primary-button" (click)="loadClusters()">Retry</button>
  </div>

  <!-- Nothing to show behind it yet: before the first run, the plain message; once it
       starts, the same progress card takes over this centered spot. -->
  <div class="empty-state" *ngIf="!isLoading && !loadError && clusters.length === 0 && !showProgressBar">
    <p class="empty-message">No clusters yet — click Clusterize to group documents first.</p>
  </div>

  <div class="empty-state progress-section-center" *ngIf="!isLoading && !loadError && clusters.length === 0 && showProgressBar">
    <ng-container *ngTemplateOutlet="progressCard"></ng-container>
  </div>

  <div class="clusters-body" *ngIf="!isLoading && !loadError && clusters.length > 0">
    <div class="clusters-count-row">
      <div class="clusters-count">{{ clustersFoundLabel }}</div>
      <div class="clusters-page-meta" *ngIf="runIdShort || totalPages > 1">
        <span *ngIf="runIdShort" class="clusters-run-id" [title]="runId || ''">ID {{ runIdShort }}</span>
        <span *ngIf="totalPages > 1">{{ pageRangeLabel }}</span>
      </div>
    </div>

    <div class="cluster-list">
      <div class="cluster-card" *ngFor="let cluster of clusters" [class.expanded]="isExpanded(cluster.cluster_id)">
        <div class="cluster-card-main">
          <div class="cluster-card-accent"></div>

          <div class="cluster-card-content">
            <div class="cluster-card-top">
              <div class="cluster-title-row">
                <h2 class="cluster-title">Cluster {{ cluster.cluster_id }}</h2>
                <span
                  class="cluster-id-badge"
                  *ngIf="runIdShort"
                  [title]="runId || ''">
                  {{ runIdShort }}
                </span>
              </div>
              <button
                class="secondary-button"
                (click)="generateForCluster(cluster); $event.stopPropagation()"
                [disabled]="generatingClusterId !== null || !cluster.documents?.length"
                title="Generate inferences for all documents in this cluster">
                {{ generatingClusterId === cluster.cluster_id ? 'Generating...' : 'Generate Inferences' }}
              </button>
            </div>

            <div class="cluster-pills">
              <span class="pill">{{ cluster.documents?.length || 0 }} source{{ (cluster.documents?.length || 0) !== 1 ? 's' : '' }}</span>
              <span class="pill">{{ cluster.chunk_count }} chunk{{ cluster.chunk_count !== 1 ? 's' : '' }}</span>
            </div>
          </div>
        </div>

        <button
          type="button"
          class="view-sources-toggle"
          (click)="toggleCluster(cluster.cluster_id)">
          <span>{{ isExpanded(cluster.cluster_id) ? 'Hide sources' : 'View sources' }}</span>
          <svg
            class="chevron"
            [class.open]="isExpanded(cluster.cluster_id)"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none">
            <path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <div class="cluster-sources" *ngIf="isExpanded(cluster.cluster_id)">
          <div class="sources-label">
            SOURCES
            <span class="sources-badge">{{ cluster.documents?.length || 0 }}</span>
          </div>

          <div class="sources-empty" *ngIf="!cluster.documents?.length">
            No documents in this cluster.
          </div>

          <div
            class="source-doc"
            *ngFor="let doc of cluster.documents"
            [class.expanded]="isSourceExpanded(cluster.cluster_id, doc.document_name)">
            <button
              type="button"
              class="source-doc-header"
              [attr.aria-expanded]="isSourceExpanded(cluster.cluster_id, doc.document_name)"
              [attr.aria-label]="sourceToggleLabel(cluster.cluster_id, doc)"
              (click)="toggleSource(cluster.cluster_id, doc.document_name)">
              <div class="source-doc-info">
                <div class="doc-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                    <path d="M7 3h7l5 5v13a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z"/>
                    <path d="M14 3v5h5"/>
                  </svg>
                </div>
                <div class="source-doc-text">
                  <div class="source-doc-name" [title]="doc.document_name">{{ doc.document_name }}</div>
                  <div class="source-doc-meta">{{ sourceMetaLabel(cluster.cluster_id, doc) }}</div>
                </div>
              </div>
              <span class="source-doc-action">
                {{ isSourceExpanded(cluster.cluster_id, doc.document_name) ? 'Hide' : 'View chunks' }}
                <svg
                  class="chevron"
                  [class.open]="isSourceExpanded(cluster.cluster_id, doc.document_name)"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  aria-hidden="true">
                  <path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </button>

            <div class="source-chunks" *ngIf="isSourceExpanded(cluster.cluster_id, doc.document_name)">
              <div class="source-chunks-loading" *ngIf="isSourceChunksLoading(cluster.cluster_id, doc.document_name)">
                <div class="loading-spinner loading-spinner-sm"></div>
                <span>Loading chunks…</span>
              </div>

              <div class="source-chunks-error" *ngIf="sourceChunksErrorFor(cluster.cluster_id, doc.document_name) as chunkError">
                <p>{{ chunkError }}</p>
                <button
                  type="button"
                  class="chunk-text-button"
                  (click)="retrySourceChunks(cluster.cluster_id, doc.document_name); $event.stopPropagation()">
                  Try again
                </button>
              </div>

              <div
                class="source-chunks-empty"
                *ngIf="!isSourceChunksLoading(cluster.cluster_id, doc.document_name)
                  && !sourceChunksErrorFor(cluster.cluster_id, doc.document_name)
                  && sourceChunksFor(cluster.cluster_id, doc.document_name).length === 0">
                No readable text for this source in this cluster.
              </div>

              <ng-container *ngIf="visibleSourceChunks(cluster.cluster_id, doc.document_name) as chunks">
                <div class="source-chunks-toolbar">
                  {{ chunks.length }} chunk{{ chunks.length !== 1 ? 's' : '' }} in this cluster
                </div>
                <article
                  class="source-chunk"
                  *ngFor="let chunk of chunks; let i = index">
                  <div class="source-chunk-header">
                    <span class="source-chunk-index">Chunk {{ i + 1 }}</span>
                    <button
                      type="button"
                      class="chunk-text-button"
                      (click)="copyChunk(cluster.cluster_id, doc.document_name, chunk, i); $event.stopPropagation()">
                      {{ copiedChunkKey === chunkKey(cluster.cluster_id, doc.document_name, chunk, i) ? 'Copied' : 'Copy' }}
                    </button>
                  </div>
                  <p class="source-chunk-text">{{ displayedChunkText(cluster.cluster_id, doc.document_name, chunk, i) }}</p>
                  <button
                    type="button"
                    class="chunk-text-button chunk-expand-button"
                    *ngIf="isChunkLong(chunk.text_content)"
                    (click)="toggleChunkText(cluster.cluster_id, doc.document_name, chunk, i); $event.stopPropagation()">
                    {{ isChunkTextExpanded(cluster.cluster_id, doc.document_name, chunk, i) ? 'Show less' : 'Show more' }}
                  </button>
                </article>
              </ng-container>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="clusters-pagination" *ngIf="totalPages > 1">
      <button
        type="button"
        class="page-button"
        (click)="goToPrevPage()"
        [disabled]="!canGoPrev"
        aria-label="Previous page">
        Previous
      </button>
      <span class="page-indicator">Page {{ page }} of {{ totalPages }}</span>
      <button
        type="button"
        class="page-button"
        (click)="goToNextPage()"
        [disabled]="!canGoNext"
        aria-label="Next page">
        Next
      </button>
    </div>
  </div>

</div>
